// Dependencies
import React from 'react';
import { connect } from 'react-redux';
import { Box, Text } from '@nerdwallet/currency';
import _ from 'lodash';
import DTITooHighModal from '../../dti-too-high-modal/dti-too-high-modal';
import CityHeader from '../../results-panel/city-header/city-header';
import DTISlider from '../../results-panel/dti-slider/dti-slider';
import HomeValue from '../../currency/home-value/home-value';
import MonthlyPayment from '../../monthly-payment/monthly-payment';

// CSS
import styles from './results-panel.module.less';
import formatOutput from './format-output';
import Breakdown from '../../breakdown/breakdown';
import {
  showInsufficientDisclaimer,
  showMinimumDisclaimer,
} from '../../monthly-payment/utils';
import { HOME_AFFORDABILITY } from '../../../lib/analytics-constants';
import { debouncedTrackInputEvent } from '../../../lib/analytics/trackers';
import useAnalytics from '../../../lib/analytics/use-analytics';
import {
  useSegmentImpressionContext,
  useSegmentImpressionHook,
} from '../../../lib/analytics/segment-impression';
import { formatResultForSegment } from '../../../lib/analytics/format-result-for-segment';

interface ResultsPanelProps {
  data: any;
  onSectionEnter?: (index: number) => void;
  className?: string;
  creditScoreCtaConfig?: any;
  DTIDetailsConfig: any;
  onChange: any;
  browser: {
    isMobile: boolean;
    isTablet: boolean;
  };
}

interface OwnProps extends ResultsPanelProps {
  isInsufficientDisclaimerVisible: boolean;
  isMinimumDisclaimerVisible: boolean;
}

const ResultsPanel = ({
  onSectionEnter,
  className,
  creditScoreCtaConfig,
  DTIDetailsConfig,
  browser,
  data,
  onChange,
  isInsufficientDisclaimerVisible,
  isMinimumDisclaimerVisible,
}: OwnProps): JSX.Element => {
  const segmentAbstract = useAnalytics();
  const childProps = {
    browser,
    data,
    onChange,
    creditScoreCtaConfig,
  };
  const minimumDisclaimer =
    'Note: Down payment used is the minimum down payment for your loan type.';
  const insufficientDisclaimer =
    'Note: The "Down payment & closing costs" you added weren\'t sufficient to cover closing costs, so these closing costs and affordability assume you do. You can also roll the VA funding fee into original mortgage balance.';
  const content = [
    {
      label: 'Monthly payment',
      content: <MonthlyPayment calculator="homeAffordability" />,
    },
    {
      label: 'Down payment and closing costs',
      content: (
        <Box>
          <MonthlyPayment calculator="homeAffordability" view="DownPayments" />
          {isInsufficientDisclaimerVisible ? (
            <Box padding={3}>
              <Text size="small">{insufficientDisclaimer}</Text>
            </Box>
          ) : null}
          {isMinimumDisclaimerVisible ? (
            <Box padding={3}>
              <Text size="small">{minimumDisclaimer}</Text>
            </Box>
          ) : null}
        </Box>
      ),
    },
  ];

  const trackCityChange = (val) => {
    // segment analytics
    debouncedTrackInputEvent(
      {
        field_type: 'user_type_input',
        entityName: 'city',
        field_value: val.city,
        field: 'City',
      },
      segmentAbstract
    );
  };

  const handleSectionChange = (index) => {
    // segment analytics
    const tabLabel = content[index].label;

    debouncedTrackInputEvent(
      {
        field_type: 'tab_switch',
        entityName: 'form_field_section',
        field_value: tabLabel,
        field: tabLabel
          .split(' ')
          .map((w) => (w.length > 3 ? _.capitalize(w) : w))
          .join(' '), // Capitalize words with more than 3 characters
      },
      segmentAbstract
    );

    onSectionEnter?.(index);
  };

  const handleSliderChange = (config, value) => {
    // segment analytics
    debouncedTrackInputEvent(
      {
        entityName: 'home_affordability',
        field: 'Home Affordability',
        field_type: 'slide',
        field_value: value,
      },
      segmentAbstract
    );

    onChange?.(config, value);
  };
  const [homeValueStr, suggestedPrice, monthlyPayment] = formatOutput(data);
  const { eventId: formViewedId } = useSegmentImpressionContext() || {};
  const { ref: featureViewedRef } = useSegmentImpressionHook({
    eventType: 'feature_viewed',
    attributes: {
      entity_name: 'form_result_text',
      section_name: 'form',
      item_position: null,
      item_copy: 'how_much_house_can_i_afford_calculator',
      content_id: null,
      additional_attributes: formatResultForSegment([
        {
          label: 'Your selected home price',
          displayValue: homeValueStr,
        },
        {
          label: 'Suggested Home Price',
          displayValue: suggestedPrice,
        },
        {
          label: 'Your Monthly Payment',
          displayValue: monthlyPayment,
        },
      ]),
      parent_event_id: formViewedId,
    },
  });

  return (
    <Box className={styles['results-panel']}>
      <DTITooHighModal {...childProps} />
      <CityHeader
        {...childProps}
        browser={browser}
        onCityChange={trackCityChange}
      />
      <div ref={featureViewedRef}>
        <HomeValue
          formattedHomeValue={homeValueStr}
          formattedSuggestedPrice={suggestedPrice}
        />
      </div>
      <DTISlider
        {...childProps}
        onChange={handleSliderChange}
        browser={browser}
        DTIDetailsConfig={DTIDetailsConfig}
      />
      <Breakdown
        className={className}
        content={content}
        onSectionChange={handleSectionChange}
      />
    </Box>
  );
};

ResultsPanel.displayName = 'ResultsPanel';

ResultsPanel.defaultProps = {
  creditScoreCtaConfig: {},
};

const mapStateToProps = (state) => {
  return {
    isInsufficientDisclaimerVisible: showInsufficientDisclaimer(
      state,
      HOME_AFFORDABILITY
    ),
    isMinimumDisclaimerVisible: showMinimumDisclaimer(
      state,
      HOME_AFFORDABILITY
    ),
  };
};

export default connect(mapStateToProps)(ResultsPanel);
